import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { components, GroupBase, MenuListProps } from 'react-select';
import { Plus } from 'lucide-react';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { TourInstallmentInputForm } from '@/features/dashboard/tourConfig/schemas/tourInstallmentsSchema.ts';
import useGetBankAccounts from '@/features/dashboard/paymentConfig/hooks/useGetBankAccounts.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { toDisplayFormat } from '@/features/dashboard/paymentConfig/types/BankAccountDto.utils.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import { cn } from '@/lib/utils.ts';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import CreateBankAccountModal from '@/features/dashboard/paymentConfig/components/CreateBankAccountModal.tsx';
import Option from '@/components/form/types/Option.ts';
import Chip from '@/components/ui/Chip.tsx';

const BankAccountSelectSection = () => {
  const { control } = useFormContext<TourInstallmentInputForm>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const bankAccountComponents = useMemo(
    () => ({
      MenuList: (props: MenuListProps<Option, false, GroupBase<Option>>) => (
        <components.MenuList {...props}>
          {props.children}
          <div
            className="-mx-2 mt-1 border-t border-neutral-200 px-2 pt-1"
            onMouseDown={(e) => e.preventDefault()}
          >
            <ActionButton
              tone="ghost"
              size="xs"
              onClick={() => {
                (document.activeElement as HTMLElement)?.blur();
                setIsCreateModalOpen(true);
              }}
            >
              <Plus />
              Dodaj konto bankowe
            </ActionButton>
          </div>
        </components.MenuList>
      ),
    }),
    [setIsCreateModalOpen],
  );

  const bankAccountSelections = useWatch({
    control,
    name: 'bankAccountSelections',
  });

  const { data: bankAccounts } = useGetBankAccounts();

  if (!bankAccountSelections || bankAccountSelections.length === 0) return null;

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>Konto bankowe do przelewu</CardTitle>
        <CardDescription>
          Wybierz konto bankowe, na które uczestnicy będą dokonywać przelewów.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1 pt-0">
        <div
          className={cn(
            '-mx-4 grid gap-2 bg-neutral-50 px-4 py-2',
            'grid-cols-[60px_1fr]',
          )}
        >
          <InputLabel label={'Waluta'} required />
          <InputLabel label={'Numer konta'} required />
        </div>
        {bankAccountSelections.map((selection, index) => {
          const accounts = (bankAccounts ?? []).filter(
            (ba) => ba.currency === selection.currency,
          );
          const options = accounts.map((ba) => {
            const accountLabel = `${toDisplayFormat(ba.accountNumber, ba.accountType)} (${ba.bankName})`;
            const markAsDefault = accounts.length > 1 && ba.isDefault;

            return {
              value: ba.id,
              name: accountLabel,
              label: markAsDefault ? (
                <span className="flex items-center gap-1">
                  {accountLabel}
                  <Chip label="Domyślne" tone="accent" fill="soft" />
                </span>
              ) : (
                accountLabel
              ),
            };
          });

          return (
            <div
              key={selection.currency}
              className={'grid grid-cols-[60px_1fr] gap-2'}
            >
              <PrimitiveInput
                value={getCurrencyLabel(selection.currency)}
                disabled
                className="h-min"
              />

              <SingleSelectInput
                fieldName={
                  `bankAccountSelections.${index}.bankAccountId` as const
                }
                control={control}
                options={options}
                placeholder="Wybierz konto bankowe"
                components={bankAccountComponents}
              />
            </div>
          );
        })}
        <div className="space-y-3"></div>
      </CardContent>
      {isCreateModalOpen && (
        <CreateBankAccountModal onClose={() => setIsCreateModalOpen(false)} />
      )}
    </Card>
  );
};

export default BankAccountSelectSection;
